if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();

        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.dataset.sectionId}`);
        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${this.dataset.sectionId}`);
        this.checkboxInput.disabled = false;
        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.dataset.sectionId}`);
        this.hiddenControlField.disabled = true;
        this.emailInput = this.querySelector(`#Recipient-email-${this.dataset.sectionId}`);
        this.nameInput = this.querySelector(`#Recipient-name-${this.dataset.sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.sectionId}`);
        this.sendonInput = this.querySelector(`#Recipient-send-on-${this.dataset.sectionId}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.dataset.sectionId}`);
        if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString();
        // Name field should always be enabled (required field)
        if (this.nameInput) this.nameInput.disabled = false;

        this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');
        this.defaultErrorHeader = this.errorMessage?.innerText;
        this.currentProductVariantId = this.dataset.productVariantId;
        
        // Find the buy button - try multiple methods for x-buy-button custom element
        const form = this.closest('form');
        if (form) {
          const formId = form.id;
          // Try to find x-buy-button by data-form-id
          this.buyButton = form.querySelector(`x-buy-button[data-form-id="${formId}"]`) || 
                          document.querySelector(`x-buy-button[data-form-id="${formId}"]`) ||
                          form.querySelector('x-buy-button');
          
          // Store original disabled state (e.g., for sold out products)
          if (this.buyButton) {
            this.originalButtonDisabled = this.buyButton.hasAttribute('disabled') && 
              this.buyButton.getAttribute('disabled') !== 'false';
            
            // Add click handler to prevent clicks when disabled
            this.buyButton.addEventListener('click', (e) => {
              const isCheckboxChecked = this.checkboxInput?.checked || false;
              const emailValue = this.emailInput?.value?.trim() || '';
              const hasValidEmail = emailValue.length > 0 && emailValue.includes('@');
              
              if (isCheckboxChecked && !hasValidEmail && !this.originalButtonDisabled) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                // Focus on email input
                if (this.emailInput) {
                  this.emailInput.focus();
                  this.emailInput.setAttribute('aria-invalid', 'true');
                }
                return false;
              }
            }, true); // Use capture phase to catch early
          }
          
          // Prevent form submission if checkbox is checked but no email
          form.addEventListener('submit', (e) => {
            const isCheckboxChecked = this.checkboxInput?.checked || false;
            const emailValue = this.emailInput?.value?.trim() || '';
            const hasValidEmail = emailValue.length > 0 && emailValue.includes('@');
            
            if (isCheckboxChecked && !hasValidEmail) {
              e.preventDefault();
              e.stopPropagation();
              // Focus on email input
              if (this.emailInput) {
                this.emailInput.focus();
                this.emailInput.setAttribute('aria-invalid', 'true');
              }
              return false;
            }
          });
        }
        
        this.addEventListener('change', this.onChange.bind(this));
        // Listen to email input changes
        if (this.emailInput) {
          this.emailInput.addEventListener('input', this.validateEmailAndToggleButton.bind(this));
          this.emailInput.addEventListener('blur', this.validateEmailAndToggleButton.bind(this));
        }
        
        this.onChange();
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      connectedCallback() {
        this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.resetRecipientForm();
          }
        });

        this.variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          (event) => {
            if (event.data.sectionId === this.dataset.sectionId) {
              this.currentProductVariantId = event.data.variant.id.toString();
            }
          }
        );

        this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.displayErrorMessage(event.message, event.errors);
          }
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }
      }

      onChange() {
        if (this.checkboxInput.checked) {
          this.enableInputFields();
          // Email is required when checkbox is checked
          if (this.emailInput) {
            this.emailInput.setAttribute('required', 'required');
          }
          this.recipientFieldsLiveRegion.innerText = FoxTheme.accessibilityStrings.recipientFormExpanded;
        } else {
          this.clearInputFields();
          this.disableInputFields();
          // Remove required from email when checkbox is unchecked
          if (this.emailInput) {
            this.emailInput.removeAttribute('required');
          }
          this.clearErrorMessage();
          this.recipientFieldsLiveRegion.innerText = FoxTheme.accessibilityStrings.recipientFormCollapsed;
        }
        // Name field should always be enabled (not required, can be empty)
        if (this.nameInput) this.nameInput.disabled = false;
        
        // Validate email and toggle button
        this.validateEmailAndToggleButton();
      }

      validateEmailAndToggleButton() {
        if (!this.buyButton) {
          // Try to find button again if not found initially
          const form = this.closest('form');
          if (form) {
            const formId = form.id;
            this.buyButton = form.querySelector(`x-buy-button[data-form-id="${formId}"]`) || 
                            document.querySelector(`x-buy-button[data-form-id="${formId}"]`) ||
                            form.querySelector('x-buy-button');
          }
          if (!this.buyButton) return;
        }
        
        const isCheckboxChecked = this.checkboxInput?.checked || false;
        const emailValue = this.emailInput?.value?.trim() || '';
        const hasValidEmail = emailValue.length > 0 && emailValue.includes('@');
        
        // If button was originally disabled (e.g., sold out), don't change it
        if (this.originalButtonDisabled) {
          return;
        }
        
        // Disable button if checkbox is checked but no email is provided
        if (isCheckboxChecked && !hasValidEmail) {
          this.buyButton.setAttribute('disabled', 'disabled');
          this.buyButton.setAttribute('aria-disabled', 'true');
          // Also prevent clicks on the custom element
          this.buyButton.style.pointerEvents = 'none';
          this.buyButton.style.opacity = '0.6';
        } else {
          // Enable button if checkbox is unchecked or email is valid
          this.buyButton.removeAttribute('disabled');
          this.buyButton.removeAttribute('aria-disabled');
          this.buyButton.style.pointerEvents = '';
          this.buyButton.style.opacity = '';
        }
      }

      inputFields() {
        return [this.emailInput, this.messageInput, this.sendonInput];
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty];
      }

      clearInputFields() {
        this.inputFields().forEach((field) => (field.value = ''));
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = false));
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = true));
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();
        this.errorMessageWrapper.hidden = false;
        if (typeof body === 'object' && body !== null) {
          this.errorMessage.innerText = this.defaultErrorHeader;
          return Object.entries(body).forEach(([key, value]) => {
            const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
            const fieldSelector = `#Recipient-${key}-${this.dataset.sectionId}`;
            let message = '';
            
            // Handle different value types
            if (Array.isArray(value)) {
              message = value.join(', ');
            } else if (typeof value === 'string') {
              message = value;
            } else if (typeof value === 'object' && value !== null) {
              // If value is an object, try to extract meaningful error message
              message = JSON.stringify(value);
            } else {
              message = String(value);
            }
            
            const errorMessageElement = this.querySelector(`#${errorMessageId}`);
            const errorTextElement = errorMessageElement?.querySelector('.error-message');
            if (!errorTextElement) return;

            if (this.errorMessageList) {
              this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message));
            }

            errorTextElement.innerText = `${message}.`;
            errorMessageElement.classList.remove('hidden');

            const inputElement = this[`${key}Input`];
            if (!inputElement) return;

            inputElement.setAttribute('aria-invalid', true);
            inputElement.setAttribute('aria-describedby', errorMessageId);
          });
        }

        // Handle string error messages
        if (typeof body === 'string') {
          this.errorMessage.innerText = body;
        } else if (body !== null && typeof body === 'object') {
          // Fallback for objects that weren't handled above
          this.errorMessage.innerText = this.defaultErrorHeader || 'An error occurred';
        }
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.setAttribute('href', target);
        a.innerText = message;
        li.appendChild(a);
        li.className = 'error-message';
        return li;
      }

      clearErrorMessage() {
        this.errorMessageWrapper.hidden = true;

        if (this.errorMessageList) this.errorMessageList.innerHTML = '';

        this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error-message');
          if (textField) textField.innerText = '';
        });

        [this.emailInput, this.messageInput, this.nameInput, this.sendonInput].forEach((inputElement) => {
          inputElement.setAttribute('aria-invalid', false);
          inputElement.removeAttribute('aria-describedby');
        });
      }

      resetRecipientForm() {
        if (this.checkboxInput.checked) {
          this.checkboxInput.checked = false;
          this.clearInputFields();
          this.clearErrorMessage();
        }
      }
    }
  );
}
